/**
 * KnowledgeBasePage - Project knowledge base management
 *
 * Features:
 * - List, create, edit, delete, enable/disable knowledge entries
 * - Re-import guidance from connected repositories
 */

import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, RefreshCw } from 'lucide-react';
import { LoadingState, Toast, ConfirmDialog } from '@/components/common';
import { KnowledgeList, KnowledgeEntryModal } from '@/components/knowledge';
import { useKnowledgeBasePage } from '../hooks/useKnowledgeBasePage';

export function KnowledgeBasePage() {
  const navigate = useNavigate();
  const { id: projectId } = useParams<{ id: string }>();
  const { query, modal, form, actions, mutations, toast } = useKnowledgeBasePage();

  return (
    <div className="space-y-6">
      <LoadingState message="Loading knowledge base..." isLoading={query.isLoading} />

      <div>
        <button
          onClick={() => navigate(`/projects/${projectId}?tab=config`)}
          className="flex items-center font-caption gap-2 text-gray-500 dark:text-white-500 mb-[0.84rem]"
        >
          <ArrowLeft className="w-4.5 h-4.5" />
          Back to Project
        </button>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="font-subtitle text-[#1E1E1E] dark:text-white">Knowledge Base</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Context the agent uses to work more accurately on this project.
            </p>
          </div>
          {query.isAdmin && (
            <div className="flex items-center gap-2">
              <button
                onClick={actions.importFromRepos}
                disabled={mutations.isImporting}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${mutations.isImporting ? 'animate-spin' : ''}`} />
                {mutations.isImporting ? 'Importing...' : 'Import from repos'}
              </button>
              <button
                onClick={actions.openCreateModal}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Entry
              </button>
            </div>
          )}
        </div>
      </div>

      {query.hasError ? (
        <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6">
          <p className="text-red-800 dark:text-red-200">Failed to load the knowledge base</p>
        </div>
      ) : (
        <KnowledgeList
          projectId={projectId!}
          entries={query.entries}
          isAdmin={query.isAdmin}
          onCreateClick={actions.openCreateModal}
          onEditClick={actions.openEditModal}
          onDeleteClick={actions.openDeleteModal}
          onToggleEnabled={actions.toggleEnabled}
        />
      )}

      <KnowledgeEntryModal
        isOpen={modal.active === 'create' || modal.active === 'edit'}
        mode={modal.active === 'edit' ? 'edit' : 'create'}
        hasExistingAttachment={modal.selectedEntry?.attachment != null}
        onClose={modal.close}
        onSave={actions.save}
        form={form.data}
        onFormChange={form.setData}
        isFormValid={form.isValid}
        isSaving={mutations.isSaving}
      />

      <ConfirmDialog
        isOpen={modal.active === 'delete'}
        onClose={modal.close}
        onConfirm={actions.delete}
        title="Delete Knowledge Entry"
        message={`Are you sure you want to delete "${modal.selectedEntry?.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
      />

      <Toast
        isOpen={toast.state.isOpen}
        onClose={toast.close}
        message={toast.state.message}
        variant={toast.state.variant}
      />
    </div>
  );
}
